/**
 * Phone Number Inventory - MISSION CONTROL ONLY
 * 
 * This component is for internal Mission Control use only.
 * It is NOT part of the business dashboard.
 * 
 * Route: /missioncontrol/phone-numbers
 * Access: Mission Control authentication required
 * 
 * Purpose: Manage phone number inventory from AWS Pinpoint
 * - Request new phone numbers from AWS Pinpoint
 * - Assign numbers to organizations/businesses
 * - Track inventory and costs
 * - Sync real-time status from Pinpoint
 * - Release unused numbers
 * 
 * Note: Business selection dropdown is for ASSIGNING numbers to organizations,
 * not for filtering. This is how you connect a phone number to a specific business.
 */

import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Typography,
  Button,
  Card,
  CardContent,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  IconButton,
  Tooltip,
  Alert,
  CircularProgress,
} from '@mui/material';
import {
  Add as AddIcon,
  Refresh as RefreshIcon,
  Assignment as AssignIcon,
  Delete as DeleteIcon,
  Phone as PhoneIcon,
  TrendingUp as StatsIcon,
} from '@mui/icons-material';
import axios from 'axios';

// Admin API client - separate from business API
// Uses custom domain with /v1 base path mapping
const adminApi = axios.create({
  baseURL: 'https://api.reachoutaiapp.com/v1',
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add request interceptor to add auth token but skip business context
adminApi.interceptors.request.use(async (config) => {
  try {
    const { fetchAuthSession } = await import('aws-amplify/auth');
    const session = await fetchAuthSession();
    const idToken = session.tokens?.idToken;
    
    if (idToken) {
      config.headers.Authorization = `Bearer ${idToken}`;
    }
  } catch (error) {
    console.error('Error getting auth token:', error);
  }
  
  return config;
});

interface PhoneNumber {
  phoneNumberId: string;
  phoneNumber: string;
  phoneNumberArn: string;
  numberType: 'TOLL_FREE' | 'LOCAL' | 'TEN_DLC';
  status: 'available' | 'assigned' | 'pending' | 'released';
  assignedTo?: string;
  assignedAt?: string;
  monthlyLeaseCost: number;
  capabilities: {
    sms: boolean;
    voice: boolean;
    mms: boolean;
  };
  createdAt: string;
  updatedAt: string;
  metadata?: {
    areaCode?: string;
    registrationId?: string;
    poolId?: string;
    assignedBusinessName?: string;
    assignedBusinessEmail?: string;
    pinpointStatus?: string;
    lastSynced?: string;
  };
}

interface Business {
  vendorId: string;
  businessName: string;
  email: string;
  phone?: string;
  status: string;
}

interface InventoryStats {
  total: number;
  available: number;
  assigned: number;
  pending: number;
  totalMonthlyCost: number;
  byType: {
    tollFree: number;
    local: number;
    tenDlc: number;
  };
}

export default function PhoneNumberInventory() {
  const [phoneNumbers, setPhoneNumbers] = useState<PhoneNumber[]>([]);
  const [stats, setStats] = useState<InventoryStats | null>(null);
  const [businesses, setBusinesses] = useState<Business[]>([]);
  const [loading, setLoading] = useState(true);
  const [businessesLoading, setBusinessesLoading] = useState(true);
  const [requestDialogOpen, setRequestDialogOpen] = useState(false);
  const [assignDialogOpen, setAssignDialogOpen] = useState(false);
  const [selectedNumber, setSelectedNumber] = useState<PhoneNumber | null>(null);
  const [filterStatus, setFilterStatus] = useState<string>('all');
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  // Request form state
  const [requestForm, setRequestForm] = useState({
    numberType: 'TOLL_FREE' as 'TOLL_FREE' | 'LOCAL' | 'TEN_DLC',
    areaCode: '',
    quantity: 1,
    registrationId: '',
  });

  // Assign form state
  const [assignForm, setAssignForm] = useState({
    vendorId: '',
  });

  // Confirmation dialog state
  const [confirmDialog, setConfirmDialog] = useState<{
    open: boolean;
    title: string;
    message: string;
    action: () => void;
  }>({ open: false, title: '', message: '', action: () => {} });

  useEffect(() => {
    loadData();
    loadBusinesses();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterStatus]);

  const loadBusinesses = async () => {
    try {
      setBusinessesLoading(true);
      const response = await adminApi.get('/admin/phone-numbers/businesses');
      setBusinesses(response.data.businesses || []);
      setBusinessesLoading(false);
    } catch (err: any) {
      console.error('Error loading businesses:', err);
      setBusinessesLoading(false);
    }
  };

  const loadData = async () => {
    try {
      setLoading(true);
      setError(null);

      // Load phone numbers
      const numbersResponse = await adminApi.get('/admin/phone-numbers', {
        params: filterStatus !== 'all' ? { status: filterStatus } : {},
      });
      setPhoneNumbers(numbersResponse.data.numbers || []);

      // Load stats
      const statsResponse = await adminApi.get('/admin/phone-numbers/stats');
      setStats(statsResponse.data.stats);

      setLoading(false);
    } catch (err: any) {
      console.error('Error loading phone numbers:', err);
      setError(err.response?.data?.error || 'Failed to load phone numbers');
      setLoading(false);
    }
  };

  const handleRequestNumber = async () => {
    try {
      setError(null);
      setSuccess(null);

      await adminApi.post('/admin/phone-numbers/request', requestForm);

      setSuccess(`Successfully requested ${requestForm.quantity} phone number(s)`);
      setRequestDialogOpen(false);
      setRequestForm({
        numberType: 'TOLL_FREE',
        areaCode: '',
        quantity: 1,
        registrationId: '',
      });
      loadData();
    } catch (err: any) {
      console.error('Error requesting phone number:', err);
      setError(err.response?.data?.error || 'Failed to request phone number');
    }
  };

  const handleOpenAssignDialog = (number: PhoneNumber) => {
    setSelectedNumber(number);
    // Pre-fill with current assignment if exists
    setAssignForm({ vendorId: number.assignedTo || '' });
    setAssignDialogOpen(true);
  };

  const handleAssignNumber = async () => {
    if (!selectedNumber) return;

    try {
      setError(null);
      setSuccess(null);

      // If vendorId is empty, unassign instead
      if (!assignForm.vendorId && selectedNumber.assignedTo) {
        await adminApi.post(`/admin/phone-numbers/${selectedNumber.phoneNumberId}/unassign`);
        setSuccess(`Phone number ${selectedNumber.phoneNumber} unassigned successfully`);
      } else if (assignForm.vendorId) {
        await adminApi.post(`/admin/phone-numbers/${selectedNumber.phoneNumberId}/assign`, {
          vendorId: assignForm.vendorId,
        });
        setSuccess(`Phone number ${selectedNumber.phoneNumber} assigned successfully`);
      }

      setAssignDialogOpen(false);
      setSelectedNumber(null);
      setAssignForm({ vendorId: '' });
      loadData();
    } catch (err: any) {
      console.error('Error assigning phone number:', err);
      setError(err.response?.data?.error || 'Failed to assign phone number');
    }
  };

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const handleUnassignNumber = async (phoneNumberId: string) => {
    if (!window.confirm('Are you sure you want to unassign this phone number?')) {
      return;
    }

    try {
      setError(null);
      setSuccess(null);

      await adminApi.post(`/admin/phone-numbers/${phoneNumberId}/unassign`);

      setSuccess('Phone number unassigned successfully');
      loadData();
    } catch (err: any) {
      console.error('Error unassigning phone number:', err);
      setError(err.response?.data?.error || 'Failed to unassign phone number');
    }
  };

  const handleReleaseNumber = (phoneNumberId: string, phoneNumber: string) => {
    setConfirmDialog({
      open: true,
      title: 'Release Phone Number',
      message: `Are you sure you want to release ${phoneNumber}? This will return the number to AWS and you will lose access to it. This action cannot be undone.`,
      action: async () => {
        try {
          setError(null);
          setSuccess(null);
          await adminApi.delete(`/admin/phone-numbers/${phoneNumberId}`);
          setSuccess(`Phone number ${phoneNumber} released successfully`);
          loadData();
        } catch (err: any) {
          console.error('Error releasing phone number:', err);
          setError(err.response?.data?.error || 'Failed to release phone number');
        }
        setConfirmDialog({ ...confirmDialog, open: false });
      },
    });
  };

  const handleSyncStatus = (phoneNumberId: string, phoneNumber: string) => {
    setConfirmDialog({
      open: true,
      title: 'Sync Phone Number Status',
      message: `Sync the current status of ${phoneNumber} from AWS Pinpoint? This will update the status to reflect the real-time state in AWS.`,
      action: async () => {
        try {
          setError(null);
          setSuccess(null);
          await adminApi.post(`/admin/phone-numbers/${phoneNumberId}/sync`);
          setSuccess(`Status synced for ${phoneNumber}`);
          loadData();
        } catch (err: any) {
          console.error('Error syncing phone number status:', err);
          setError(err.response?.data?.error || 'Failed to sync phone number status');
        }
        setConfirmDialog({ ...confirmDialog, open: false });
      },
    });
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'available':
        return 'success';
      case 'assigned':
        return 'primary';
      case 'pending':
        return 'warning';
      case 'released':
        return 'error';
      default:
        return 'default';
    }
  };

  const getStatusLabel = (number: PhoneNumber) => {
    if (number.status === 'pending' && number.metadata?.pinpointStatus) {
      return `${number.status.toUpperCase()} (${number.metadata.pinpointStatus})`;
    }
    return number.status.toUpperCase();
  };

  const getNumberTypeLabel = (type: string) => {
    switch (type) {
      case 'TOLL_FREE':
        return 'Toll-Free';
      case 'LOCAL':
        return 'Local';
      case 'TEN_DLC':
        return '10DLC';
      default:
        return type;
    }
  };

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: '#0a0e27', color: '#fff', py: 4 }}>
      <Container maxWidth="xl">
        {/* Header */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 4 }}>
          <Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1 }}>
              <Typography variant="h3" fontWeight={700}>
                📞 Phone Number Inventory
              </Typography>
              <Chip 
                label="MISSION CONTROL" 
                size="small"
                sx={{ 
                  bgcolor: '#FF6B6B', 
                  color: 'white', 
                  fontWeight: 700,
                  fontSize: '0.7rem',
                  letterSpacing: '0.05em'
                }} 
              />
            </Box>
            <Typography variant="body1" color="rgba(255,255,255,0.7)">
              Admin Console - Manage AWS Pinpoint phone number inventory
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', gap: 2 }}>
            <Button
              variant="outlined"
              startIcon={<RefreshIcon />}
              onClick={loadData}
              sx={{ borderColor: '#00D4FF', color: '#00D4FF' }}
            >
              Refresh
            </Button>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => setRequestDialogOpen(true)}
              sx={{
                background: 'linear-gradient(135deg, #0066FF 0%, #00D4FF 100%)',
                fontWeight: 600,
              }}
            >
              Request Number
            </Button>
          </Box>
        </Box>

        {/* Alerts */}
        {error && (
          <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}
        {success && (
          <Alert severity="success" sx={{ mb: 3 }} onClose={() => setSuccess(null)}>
            {success}
          </Alert>
        )}

        {/* Stats Cards */}
        {stats && (
          <Grid container spacing={3} mb={4}>
            <Grid item xs={12} md={3}>
              <Card sx={{ bgcolor: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.1)' }}>
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <PhoneIcon sx={{ fontSize: 40, color: '#00D4FF' }} />
                    <Box>
                      <Typography variant="h4" fontWeight={700} color="white">
                        {stats.total}
                      </Typography>
                      <Typography variant="body2" color="rgba(255,255,255,0.6)">
                        Total Numbers
                      </Typography>
                    </Box>
                  </Box>
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12} md={3}>
              <Card sx={{ bgcolor: 'rgba(0,200,150,0.1)', border: '1px solid rgba(0,200,150,0.3)' }}>
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <StatsIcon sx={{ fontSize: 40, color: '#00C896' }} />
                    <Box>
                      <Typography variant="h4" fontWeight={700} color="#00C896">
                        {stats.available}
                      </Typography>
                      <Typography variant="body2" color="rgba(255,255,255,0.6)">
                        Available
                      </Typography>
                    </Box>
                  </Box>
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12} md={3}>
              <Card sx={{ bgcolor: 'rgba(0,102,255,0.1)', border: '1px solid rgba(0,102,255,0.3)' }}>
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <AssignIcon sx={{ fontSize: 40, color: '#0066FF' }} />
                    <Box>
                      <Typography variant="h4" fontWeight={700} color="#0066FF">
                        {stats.assigned}
                      </Typography>
                      <Typography variant="body2" color="rgba(255,255,255,0.6)">
                        Assigned
                      </Typography>
                    </Box>
                  </Box>
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12} md={3}>
              <Card sx={{ bgcolor: 'rgba(255,107,107,0.1)', border: '1px solid rgba(255,107,107,0.3)' }}>
                <CardContent>
                  <Typography variant="h4" fontWeight={700} color="#FF6B6B">
                    ${stats.totalMonthlyCost.toFixed(2)}
                  </Typography>
                  <Typography variant="body2" color="rgba(255,255,255,0.6)">
                    Monthly Cost
                  </Typography>
                  <Box sx={{ display: 'flex', gap: 2, mt: 2 }}>
                    <Typography variant="caption" color="rgba(255,255,255,0.5)">
                      Toll-Free: {stats.byType.tollFree}
                    </Typography>
                    <Typography variant="caption" color="rgba(255,255,255,0.5)">
                      Local: {stats.byType.local}
                    </Typography>
                    <Typography variant="caption" color="rgba(255,255,255,0.5)">
                      10DLC: {stats.byType.tenDlc}
                    </Typography>
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        )}

        {/* Filter */}
        <Box sx={{ mb: 3 }}>
          <FormControl sx={{ minWidth: 200 }}>
            <InputLabel sx={{ color: 'rgba(255,255,255,0.7)' }}>Filter by Status</InputLabel>
            <Select
              value={filterStatus}
              onChange={(e) => setFilterStatus(e.target.value)}
              label="Filter by Status"
              sx={{
                color: 'white',
                '.MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(255,255,255,0.3)' },
                '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: '#00D4FF' },
              }}
            >
              <MenuItem value="all">All</MenuItem>
              <MenuItem value="available">Available</MenuItem>
              <MenuItem value="assigned">Assigned</MenuItem>
              <MenuItem value="pending">Pending</MenuItem>
            </Select>
          </FormControl>
        </Box>

        {/* Phone Numbers Table */}
        <TableContainer
          component={Paper}
          sx={{ bgcolor: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.1)' }}
        >
          <Table>
            <TableHead>
              <TableRow sx={{ bgcolor: 'rgba(255,255,255,0.03)' }}>
                <TableCell sx={{ color: 'rgba(255,255,255,0.9)', fontWeight: 600 }}>Phone Number</TableCell>
                <TableCell sx={{ color: 'rgba(255,255,255,0.9)', fontWeight: 600 }}>Type</TableCell>
                <TableCell sx={{ color: 'rgba(255,255,255,0.9)', fontWeight: 600 }}>Status</TableCell>
                <TableCell sx={{ color: 'rgba(255,255,255,0.9)', fontWeight: 600 }}>Assigned To</TableCell>
                <TableCell sx={{ color: 'rgba(255,255,255,0.9)', fontWeight: 600 }}>Capabilities</TableCell>
                <TableCell sx={{ color: 'rgba(255,255,255,0.9)', fontWeight: 600 }}>Monthly Cost</TableCell>
                <TableCell sx={{ color: 'rgba(255,255,255,0.9)', fontWeight: 600 }}>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={7} align="center" sx={{ py: 8 }}>
                    <CircularProgress sx={{ color: '#00D4FF' }} />
                  </TableCell>
                </TableRow>
              ) : phoneNumbers.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7} align="center" sx={{ py: 8, color: 'rgba(255,255,255,0.5)' }}>
                    No phone numbers found. Request a new number to get started.
                  </TableCell>
                </TableRow>
              ) : (
                phoneNumbers.map((number) => (
                  <TableRow 
                    key={number.phoneNumberId}
                    sx={{ 
                      '&:hover': { 
                        bgcolor: 'rgba(0, 212, 255, 0.05)',
                      } 
                    }}
                  >
                    <TableCell sx={{ color: 'white', fontFamily: 'monospace', fontSize: 16 }}>
                      {number.phoneNumber}
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={getNumberTypeLabel(number.numberType)}
                        size="small"
                        sx={{ bgcolor: 'rgba(123,97,255,0.2)', color: '#7B61FF' }}
                      />
                    </TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Chip
                          label={getStatusLabel(number)}
                          size="small"
                          color={getStatusColor(number.status) as any}
                        />
                        {number.metadata?.lastSynced && (
                          <Tooltip title={`Last synced: ${new Date(number.metadata.lastSynced).toLocaleString()}`}>
                            <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.4)', fontSize: 10 }}>
                              ✓
                            </Typography>
                          </Tooltip>
                        )}
                      </Box>
                    </TableCell>
                    <TableCell>
                      {number.assignedTo ? (
                        <Box>
                          <Typography variant="body2" sx={{ color: 'white', fontWeight: 500 }}>
                            {number.metadata?.assignedBusinessName || 'Unknown Business'}
                          </Typography>
                          <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.5)' }}>
                            {number.metadata?.assignedBusinessEmail || number.assignedTo}
                          </Typography>
                        </Box>
                      ) : (
                        <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.5)' }}>
                          -
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', gap: 1 }}>
                        {number.capabilities.sms && <Chip label="SMS" size="small" sx={{ fontSize: 10 }} />}
                        {number.capabilities.voice && <Chip label="Voice" size="small" sx={{ fontSize: 10 }} />}
                        {number.capabilities.mms && <Chip label="MMS" size="small" sx={{ fontSize: 10 }} />}
                      </Box>
                    </TableCell>
                    <TableCell sx={{ color: 'white' }}>${number.monthlyLeaseCost.toFixed(2)}</TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', gap: 1 }}>
                        <Tooltip title="Assign to Business">
                          <IconButton
                            size="small"
                            onClick={() => handleOpenAssignDialog(number)}
                            sx={{ color: '#00D4FF' }}
                            disabled={number.status !== 'available' && number.status !== 'assigned'}
                          >
                            <AssignIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Sync Status from AWS Pinpoint">
                          <IconButton
                            size="small"
                            onClick={() => handleSyncStatus(number.phoneNumberId, number.phoneNumber)}
                            sx={{ color: '#7B61FF' }}
                          >
                            <RefreshIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        {number.status === 'available' && (
                          <Tooltip title="Release Number Back to AWS">
                            <IconButton
                              size="small"
                              onClick={() => handleReleaseNumber(number.phoneNumberId, number.phoneNumber)}
                              sx={{ color: '#FF6B6B' }}
                            >
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        )}
                      </Box>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>

        {/* Request Number Dialog */}
        <Dialog
          open={requestDialogOpen}
          onClose={() => setRequestDialogOpen(false)}
          maxWidth="sm"
          fullWidth
          PaperProps={{ sx: { bgcolor: '#1a1e3a', color: 'white' } }}
        >
          <DialogTitle>Request New Phone Number</DialogTitle>
          <DialogContent>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3, pt: 2 }}>
              <FormControl fullWidth>
                <InputLabel sx={{ color: 'rgba(255,255,255,0.7)' }}>Number Type</InputLabel>
                <Select
                  value={requestForm.numberType}
                  onChange={(e) => setRequestForm({ ...requestForm, numberType: e.target.value as any })}
                  label="Number Type"
                  sx={{ color: 'white' }}
                >
                  <MenuItem value="TOLL_FREE">Toll-Free</MenuItem>
                  <MenuItem value="LOCAL">Local</MenuItem>
                  <MenuItem value="TEN_DLC">10DLC</MenuItem>
                </Select>
              </FormControl>

              {requestForm.numberType === 'LOCAL' && (
                <TextField
                  label="Area Code (Optional)"
                  value={requestForm.areaCode}
                  onChange={(e) => setRequestForm({ ...requestForm, areaCode: e.target.value })}
                  placeholder="e.g., 415"
                  fullWidth
                  sx={{ input: { color: 'white' } }}
                />
              )}

              <TextField
                label="Quantity"
                type="number"
                value={requestForm.quantity}
                onChange={(e) => setRequestForm({ ...requestForm, quantity: parseInt(e.target.value) || 1 })}
                inputProps={{ min: 1, max: 10 }}
                fullWidth
                sx={{ input: { color: 'white' } }}
              />

              {requestForm.numberType === 'TEN_DLC' && (
                <TextField
                  label="Registration ID"
                  value={requestForm.registrationId}
                  onChange={(e) => setRequestForm({ ...requestForm, registrationId: e.target.value })}
                  placeholder="Required for 10DLC numbers"
                  fullWidth
                  required
                  sx={{ input: { color: 'white' } }}
                />
              )}
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setRequestDialogOpen(false)} sx={{ color: 'rgba(255,255,255,0.7)' }}>
              Cancel
            </Button>
            <Button
              onClick={handleRequestNumber}
              variant="contained"
              sx={{ background: 'linear-gradient(135deg, #0066FF 0%, #00D4FF 100%)' }}
            >
              Request Number
            </Button>
          </DialogActions>
        </Dialog>

        {/* Edit Phone Number Assignment Dialog */}
        <Dialog
          open={assignDialogOpen}
          onClose={() => setAssignDialogOpen(false)}
          maxWidth="sm"
          fullWidth
          PaperProps={{ sx: { bgcolor: '#1a1e3a', color: 'white' } }}
        >
          <DialogTitle>
            <Box>
              <Typography variant="h6">Edit Phone Number Assignment</Typography>
              <Typography variant="body2" color="rgba(255,255,255,0.5)" mt={1}>
                {selectedNumber?.phoneNumber}
              </Typography>
            </Box>
          </DialogTitle>
          <DialogContent>
            <Box sx={{ pt: 2 }}>
              {selectedNumber?.assignedTo && (
                <Alert 
                  severity="info" 
                  sx={{ 
                    mb: 3, 
                    bgcolor: 'rgba(0, 212, 255, 0.1)', 
                    color: 'white',
                    '& .MuiAlert-icon': { color: '#00D4FF' }
                  }}
                >
                  Currently assigned to: <strong>{selectedNumber.metadata?.assignedBusinessName || selectedNumber.assignedTo}</strong>
                </Alert>
              )}
              
              {businessesLoading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                  <CircularProgress size={24} sx={{ color: '#00D4FF' }} />
                </Box>
              ) : (
                <FormControl fullWidth>
                  <InputLabel sx={{ color: 'rgba(255,255,255,0.7)' }}>Assign to Organization</InputLabel>
                  <Select
                    value={assignForm.vendorId}
                    onChange={(e) => setAssignForm({ vendorId: e.target.value })}
                    label="Assign to Organization"
                    sx={{
                      color: 'white',
                      '.MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(255,255,255,0.3)' },
                      '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: '#00D4FF' },
                      '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: '#00D4FF' },
                    }}
                  >
                    <MenuItem value="">
                      <Typography variant="body2" color="rgba(255,255,255,0.5)" fontStyle="italic">
                        -- Unassigned --
                      </Typography>
                    </MenuItem>
                    {businesses.map((business) => (
                      <MenuItem key={business.vendorId} value={business.vendorId}>
                        <Box>
                          <Typography variant="body1">{business.businessName}</Typography>
                          <Typography variant="caption" color="rgba(255,255,255,0.5)">
                            {business.email}
                          </Typography>
                        </Box>
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              )}
              
              <Typography variant="caption" color="rgba(255,255,255,0.5)" sx={{ display: 'block', mt: 2 }}>
                Select an organization to assign this number, or choose "Unassigned" to remove the current assignment.
              </Typography>
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setAssignDialogOpen(false)} sx={{ color: 'rgba(255,255,255,0.7)' }}>
              Cancel
            </Button>
            <Button
              onClick={handleAssignNumber}
              variant="contained"
              disabled={businessesLoading}
              sx={{ background: 'linear-gradient(135deg, #0066FF 0%, #00D4FF 100%)' }}
            >
              Save Assignment
            </Button>
          </DialogActions>
        </Dialog>

        {/* Confirmation Dialog */}
        <Dialog
          open={confirmDialog.open}
          onClose={() => setConfirmDialog({ ...confirmDialog, open: false })}
          maxWidth="sm"
          fullWidth
          PaperProps={{
            sx: {
              bgcolor: '#1a1f3a',
              color: 'white',
              border: '1px solid rgba(0, 212, 255, 0.3)',
            },
          }}
        >
          <DialogTitle sx={{ borderBottom: '1px solid rgba(255,255,255,0.1)' }}>
            {confirmDialog.title}
          </DialogTitle>
          <DialogContent sx={{ mt: 2 }}>
            <Typography variant="body1" sx={{ color: 'rgba(255,255,255,0.9)' }}>
              {confirmDialog.message}
            </Typography>
          </DialogContent>
          <DialogActions>
            <Button 
              onClick={() => setConfirmDialog({ ...confirmDialog, open: false })} 
              sx={{ color: 'rgba(255,255,255,0.7)' }}
            >
              Cancel
            </Button>
            <Button
              onClick={confirmDialog.action}
              variant="contained"
              sx={{
                bgcolor: '#00D4FF',
                color: '#0a0e27',
                '&:hover': { bgcolor: '#00B8E6' },
              }}
            >
              Confirm
            </Button>
          </DialogActions>
        </Dialog>
      </Container>
    </Box>
  );
}
